// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { isThemeActive, Theme } from '@cloudscape-design/component-toolkit/internal';
import { getAnalyticsMetadataAttribute } from '@cloudscape-design/component-toolkit/internal/analytics-metadata';
import Dropdown from '../../dropdown/internal';
import InternalIcon from '../../icon/internal';
import useHiddenDescription from '../../internal/hooks/use-hidden-description';
import { useVisualRefresh } from '../../internal/hooks/use-visual-mode';
import ItemsList from '../items-list';
import Tooltip from '../tooltip.js';
import { getMenuItemProps } from '../utils/menu-item';
import styles from './styles.css.js';
const ExpandableCategoryElement = ({ index, item, onItemActivate, onGroupToggle, targetItem, isHighlighted, isKeyboardHighlight, isExpanded, lastInDropdown, highlightItem, disabled, expandToViewport, variant, position, renderItem, filteringText, filteringEnabled, menuId, filteringDescriptionId, }) => {
    var _a;
    const highlighted = isHighlighted(item);
    const expanded = isExpanded(item);
    const isKeyboardHighlighted = isKeyboardHighlight(item);
    const triggerRef = React.useRef(null);
    const ref = useRef(null);
    useEffect(() => {
        if (triggerRef.current && highlighted && !expanded && !filteringEnabled) {
            triggerRef.current.focus();
        }
    }, [expanded, highlighted, filteringEnabled]);
    const onClick = event => {
        var _a;
        if (!disabled) {
            event.preventDefault();
            onGroupToggle(item, event);
            if (!filteringEnabled) {
                (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
    };
    const onMouseDown = event => {
        // Ensure that focus remains on the filtering input at all times.
        if (filteringEnabled) {
            event.preventDefault();
        }
    };
    const onHover = () => {
        highlightItem(item);
    };
    const isVisualRefresh = useVisualRefresh();
    const isOneTheme = isThemeActive(Theme.OneTheme);
    const isDisabledWithReason = !!item.disabledReason && item.disabled;
    const { targetProps, descriptionEl } = useHiddenDescription(item.disabledReason);
    const groupProps = {
        index: index !== null && index !== void 0 ? index : 0,
        type: 'group',
        option: item,
        disabled: !!disabled,
        highlighted: !!highlighted,
        expanded: expanded,
        expandDirection: 'horizontal',
    };
    const renderResult = (_a = renderItem === null || renderItem === void 0 ? void 0 : renderItem({ item: groupProps, filterText: filteringText })) !== null && _a !== void 0 ? _a : null;
    const trigger = item.text && (React.createElement("span", { id: menuId && item.id ? `${menuId}-${item.id}` : undefined, className: clsx(styles.header, styles['expandable-header'], styles[`variant-${variant}`], {
            [styles.disabled]: disabled,
            [styles.highlighted]: highlighted,
            [styles['no-content-styling']]: !!renderResult,
            [styles['is-focused']]: isKeyboardHighlighted,
            [styles['visual-refresh']]: isVisualRefresh,
        }), 
        // When filtering is enabled, we use aria-activedescendant on the filter input and provide
        // the `id` of the item to select it. When filtering is disabled, we are using the roving
        // tabindex technique to manage the focus state of the dropdown. The current element will
        // have tabindex=0 which means that it can be tabbed to, while all other items have
        // tabindex=-1 so we can focus them when necessary.
        tabIndex: filteringEnabled ? -1 : highlighted ? 0 : -1, ref: triggerRef, "data-awsui-motion-trigger": "hover", ...getMenuItemProps({ parent: true, expanded, disabled }), ...(isDisabledWithReason ? targetProps : {}), ...getAnalyticsMetadataAttribute(disabled
            ? {}
            : {
                action: !expanded ? 'expand' : 'collapse',
                detail: {
                    position: position || '0',
                    label: { root: 'self' },
                    id: item.id || '',
                },
            }) }, renderResult ? (renderResult) : (React.createElement(React.Fragment, null,
        (item.iconName || item.iconUrl || item.iconSvg) && (React.createElement("span", { className: styles['icon-wrapper'] },
            React.createElement(InternalIcon, { name: item.iconName, url: item.iconUrl, svg: item.iconSvg, alt: item.iconAlt }))),
        React.createElement("span", null, item.text),
        React.createElement("span", { className: clsx(styles['expand-icon'], styles['expand-icon-right']) },
            React.createElement(InternalIcon, { name: isOneTheme ? 'angle-down' : 'caret-down-filled', size: isOneTheme ? 'x-small' : 'normal', nativeAttributes: { 'data-awsui-motion-target': '' } }))))));
    let content;
    // If the category element is disabled, we do not render a dropdown.
    // Screenreaders are confused by additional sections
    if (isDisabledWithReason) {
        content = (React.createElement(Tooltip, { content: item.disabledReason },
            trigger,
            descriptionEl));
    }
    else if (disabled) {
        content = trigger;
    }
    else {
        content = (React.createElement(Dropdown, { open: expanded, interior: true, hideBlockBorder: false, expandToViewport: expandToViewport, trigger: trigger, content: item.items && expanded ? (React.createElement("ul", { role: "menu", "aria-label": item.text, className: clsx(styles['items-list-container'], styles['in-dropdown']) },
                React.createElement(ItemsList, { items: item.items, onItemActivate: onItemActivate, onGroupToggle: onGroupToggle, targetItem: targetItem, isHighlighted: isHighlighted, isKeyboardHighlight: isKeyboardHighlight, isExpanded: isExpanded, lastInDropdown: lastInDropdown, highlightItem: highlightItem, variant: variant, position: position, renderItem: renderItem, parentProps: groupProps, filteringText: filteringText, filteringEnabled: filteringEnabled, menuId: menuId, filteringDescriptionId: filteringDescriptionId }))) : undefined }));
    }
    return (React.createElement("li", { className: clsx(styles.category, styles[`variant-${variant}`], styles.expandable, {
            [styles.expanded]: expanded,
            [styles.disabled]: disabled,
            [styles.highlighted]: highlighted,
        }), role: "presentation", "data-testid": item.id, ref: ref, onClick: onClick, onMouseDown: onMouseDown, onMouseEnter: onHover, onTouchStart: onHover }, content));
};
export default ExpandableCategoryElement;
//# sourceMappingURL=expandable-category-element.js.map